import type {
  PatientAlertResponse,
  PatientHistoryEntryResponse,
  PatientLastVitalsResponse,
  PatientNoteEntryResponse,
} from '../infrastructure/patients.api';

/**
 * Domain — pure presentation helpers for the patients feature.
 *
 * Narrows raw wire strings (status, alert type/status) to local unions and
 * derives display-ordering rules. Pure functions only: no React, no API, no
 * browser APIs.
 */

export type PatientStatus = 'active' | 'archived';

const PATIENT_STATUS_MAP: Record<string, PatientStatus> = {
  active: 'active',
  archived: 'archived',
};

/** Maps a raw patient status ("Active"/"Archived") to a local union. */
export function patientStatus(status: string): PatientStatus {
  return PATIENT_STATUS_MAP[status.toLowerCase()] ?? 'active';
}

export type AlertType = 'allergy' | 'critical' | 'warning' | 'note';
export type AlertStatus = 'active' | 'resolved';

export function alertType(type: string): AlertType {
  const value = type.toLowerCase();
  if (value === 'allergy' || value === 'critical' || value === 'warning' || value === 'note') {
    return value;
  }
  return 'note';
}

export function alertStatus(status: string): AlertStatus {
  return status.toLowerCase() === 'resolved' ? 'resolved' : 'active';
}

/** True for a still-active alert. */
export function isActiveAlert(alert: PatientAlertResponse): boolean {
  return alertStatus(alert.status) === 'active';
}

// Display prominence: allergies and critical warnings surface first.
const ALERT_PROMINENCE: Record<AlertType, number> = {
  allergy: 0,
  critical: 1,
  warning: 2,
  note: 3,
};

/**
 * Orders a patient's alerts for the chart: active before resolved, then by
 * clinical prominence (allergy → critical → warning → note).
 */
export function orderAlertsForDisplay(alerts: PatientAlertResponse[]): PatientAlertResponse[] {
  return [...alerts].sort((a, b) => {
    const activeDiff = Number(isActiveAlert(b)) - Number(isActiveAlert(a));
    if (activeDiff !== 0) return activeDiff;
    return ALERT_PROMINENCE[alertType(a.type)] - ALERT_PROMINENCE[alertType(b.type)];
  });
}

export type HistoryCategory = 'condition' | 'medication' | 'surgery' | 'immunization';
export type HistoryEntryStatus = 'active' | 'resolved';

const HISTORY_CATEGORY_MAP: Record<string, HistoryCategory> = {
  condition: 'condition',
  medication: 'medication',
  surgery: 'surgery',
  immunization: 'immunization',
};

/** Maps a raw history-entry category ("Condition"/"Medication"/…) to a local union. */
export function historyCategory(category: string): HistoryCategory {
  return HISTORY_CATEGORY_MAP[category.toLowerCase()] ?? 'condition';
}

export function historyStatus(status: string): HistoryEntryStatus {
  return status.toLowerCase() === 'resolved' ? 'resolved' : 'active';
}

/**
 * Orders a patient's medical history entries for the chart: active before
 * resolved within each category, categories kept in encounter order.
 */
export function orderHistoryForDisplay(entries: PatientHistoryEntryResponse[]): PatientHistoryEntryResponse[] {
  return [...entries].sort((a, b) => {
    const activeDiff = Number(historyStatus(b.status) === 'active') - Number(historyStatus(a.status) === 'active');
    if (activeDiff !== 0) return activeDiff;
    return 0;
  });
}

export type NoteCategory = 'familyHistory' | 'lifestyleSocial';

const NOTE_CATEGORY_MAP: Record<string, NoteCategory> = {
  familyhistory: 'familyHistory',
  lifestylesocial: 'lifestyleSocial',
};

/** Maps a raw note category ("FamilyHistory"/"LifestyleSocial") to a local union. */
export function noteCategory(category: string): NoteCategory {
  return NOTE_CATEGORY_MAP[category.toLowerCase()] ?? 'familyHistory';
}

/** Notes belonging to one category (family history, or lifestyle/social), in server order. */
export function notesForCategory(notes: PatientNoteEntryResponse[], category: NoteCategory): PatientNoteEntryResponse[] {
  return notes.filter((note) => noteCategory(note.category) === category);
}

export const GENDERS = ['Male', 'Female', 'Other'] as const;

/** Blood-group enum name → display label (matches Domain BloodGroup names). */
export const BLOOD_GROUPS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'APos', label: 'A+' },
  { value: 'ANeg', label: 'A−' },
  { value: 'BPos', label: 'B+' },
  { value: 'BNeg', label: 'B−' },
  { value: 'AbPos', label: 'AB+' },
  { value: 'AbNeg', label: 'AB−' },
  { value: 'OPos', label: 'O+' },
  { value: 'ONeg', label: 'O−' },
];

const BLOOD_GROUP_LABELS: Record<string, string> = Object.fromEntries(
  BLOOD_GROUPS.map((group) => [group.value, group.label])
);

/** Display label for a blood-group enum name; null for Unknown/blank. */
export function bloodGroupLabel(value: string | null | undefined): string | null {
  if (!value || value === 'Unknown') return null;
  return BLOOD_GROUP_LABELS[value] ?? value;
}

// Stable, locale-aware short date for list rows (day-month-year). Time of day is intentionally omitted.
const LIST_DATE_FORMAT = new Intl.DateTimeFormat(undefined, { day: '2-digit', month: 'short', year: 'numeric' });

/** Short display date (e.g. "05 Jan 2026") for an ISO registration timestamp; em dash when absent/invalid. */
export function formatRegisteredDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '—' : LIST_DATE_FORMAT.format(date);
}

/** BP/Pulse/Temp formatted for display; each is null when its underlying reading is absent. */
export interface FormattedVitals {
  bp: string | null;
  pulse: string | null;
  temp: string | null;
}

/** Formats a last-visit vitals payload into display strings, shared by the desktop and mobile chart cards. */
export function formatVitals(vitals: PatientLastVitalsResponse | null | undefined): FormattedVitals {
  return {
    bp: vitals?.systolic != null && vitals?.diastolic != null ? `${vitals.systolic}/${vitals.diastolic}` : null,
    pulse: vitals?.pulse != null ? String(vitals.pulse) : null,
    temp: vitals?.temperature != null ? `${vitals.temperature}°${vitals.temperatureUnit ?? ''}` : null,
  };
}

/** Uppercase initials (max 2) for the avatar placeholder. */
export function patientInitials(name: string): string {
  const parts = name.split(/\s+/).filter(Boolean);
  const letters = parts.slice(0, 2).map((part) => part[0]).join('');
  return (letters || name.slice(0, 2)).toUpperCase();
}
